import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, X } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  value?: string;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  options: SelectOption[];
  disabled?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
  dropdownWidth?: number;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  placeholder,
  className = "",
  options = [],
  disabled = false,
  allowClear = false,
  showSearch = false,
  dropdownWidth = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Memoize the close dropdown handler
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
  }, []);

  // Handle clicks outside using a capture phase listener
  useEffect(() => {
    if (!isOpen) return;

    function handleGlobalClick(e: MouseEvent) {
      const selectEl = wrapperRef.current;
      const dropdownEl = dropdownRef.current;
      const targetEl = e.target as Node;

      const isOutsideSelect = selectEl && !selectEl.contains(targetEl);
      const isOutsideDropdown = dropdownEl && !dropdownEl.contains(targetEl);

      if (isOutsideSelect && isOutsideDropdown) {
        setTimeout(() => closeDropdown(), 0);
      }
    }

    document.addEventListener("click", handleGlobalClick, true);

    return () => {
      document.removeEventListener("click", handleGlobalClick, true);
    };
  }, [isOpen, closeDropdown]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDropdown();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, closeDropdown]);

  // Calculate dropdown position
  const calculateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const filteredOpts = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    const dropdownHeight = Math.min(240, filteredOpts.length * 36 + 60);

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top = buttonRect.bottom + 4;
    let left = buttonRect.left;
    const width = Math.min(dropdownWidth, buttonRect.width);

    if (
      spaceBelow < dropdownHeight &&
      spaceAbove > dropdownHeight &&
      spaceAbove > spaceBelow
    ) {
      top = buttonRect.top - dropdownHeight;
    }

    // Ensure dropdown stays within viewport boundaries
    if (left + width > viewportWidth - 10) {
      left = viewportWidth - width - 10;
    }
    if (left < 10) {
      left = 10;
    }
    if (top + dropdownHeight > viewportHeight - 10) {
      top = viewportHeight - dropdownHeight - 10;
    }
    if (top < 10) {
      top = 10;
    }

    setDropdownPosition({
      top,
      left,
      width,
    });
  }, [options, searchTerm, dropdownWidth]);

  // Recalculate position when dropdown opens or content changes
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();

      const handleResize = () => calculateDropdownPosition();
      const handleScrollRAF = () => {
        requestAnimationFrame(calculateDropdownPosition);
      };

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScrollRAF, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScrollRAF, true);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get display value
  const getDisplayValue = () => {
    const selectedOption = options.find((opt) => opt.value === value);
    return selectedOption ? selectedOption.label : "";
  };

  // Handle selection
  const handleSelect = (optValue: string) => {
    onChange?.(optValue);
    setIsOpen(false);
  };

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      {/* Select Control */}
      <div
        ref={buttonRef}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) {
              calculateDropdownPosition();
            }
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md outline-none cursor-pointer ${disabled ? "bg-gray-100 dark:bg-darkCardBackground cursor-not-allowed" : ""}  
          ${isOpen ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800" : "border-gray-300 dark:border-gray-700"} 
          focus:outline-none dark:text-white transition-all duration-150 ${className}`}
        style={{ minWidth: '200px', maxWidth: '100%' }}
      >
        <span
          className={`truncate flex-1 min-w-0 ${!value && placeholder ? "text-gray-400 dark:text-gray-500" : ""}`}
          title={getDisplayValue() || placeholder}
        >
          {showSearch && isOpen ? (
            <input
              type="text"
              className="w-full bg-transparent border-none focus:outline-none dark:text-white text-sm"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            getDisplayValue() || placeholder
          )}
        </span>
        
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {allowClear && value && (
            <X
              size={14}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* Dropdown rendered with Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[99999] bg-white dark:bg-darkCardBackground border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: "240px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-y-auto max-h-[240px]">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 
                    ${value === option.value ? "bg-blue-50 dark:bg-blue-900/30" : ""}
                    ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() =>
                      !option.disabled && handleSelect(option.value)
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="dark:text-white truncate flex-1" title={option.label}>
                        {option.label}
                      </span>
                      {value === option.value && (
                        <Check size={16} className="text-blue-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}; 